import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export function Login() {
  const { login, t } = useApp();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      nav(`/${u.role}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen abdan-hero-grid grid place-items-center px-4">
      <Card className="w-full max-w-md shadow-xl border-slate-200">
        <CardHeader className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 justify-center mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white font-black">A</div>
            <span className="font-extrabold text-xl">Abdan<span className="text-emerald-600">Pro</span></span>
          </Link>
          <CardTitle className="text-2xl">{t("login")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>{t("email")}</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="login-email" /></div>
            <div><Label>{t("password")}</Label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required data-testid="login-password" /></div>
            <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="login-submit">
              {loading ? "..." : t("login")}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm text-slate-500">
            No account? <Link to="/register" className="text-emerald-600 font-medium">{t("register")}</Link>
          </div>
          <div className="mt-6 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 space-y-1">
            <div className="font-semibold text-slate-700">Demo accounts (password shown):</div>
            <div>Admin: abdansyukri@gmail.com / Admin@123</div>
            <div>Partner: partner@abdanpro.com / Partner@123</div>
            <div>Driver: driver@abdanpro.com / Driver@123</div>
            <div>Customer: customer@abdanpro.com / Customer@123</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function Register() {
  const { register, t } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "customer", phone: "", company: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Welcome, ${u.name}`);
      nav(`/${u.role}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Register failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen abdan-hero-grid grid place-items-center px-4 py-10">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 justify-center mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white font-black">A</div>
            <span className="font-extrabold text-xl">Abdan<span className="text-emerald-600">Pro</span></span>
          </Link>
          <CardTitle className="text-2xl">{t("register")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>{t("name")}</Label><Input value={form.name} onChange={update("name")} required data-testid="reg-name" /></div>
            <div><Label>{t("email")}</Label><Input type="email" value={form.email} onChange={update("email")} required data-testid="reg-email" /></div>
            <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={update("phone")} data-testid="reg-phone" /></div>
            <div><Label>{t("password")}</Label><Input type="password" value={form.password} onChange={update("password")} required minLength={6} data-testid="reg-password" /></div>
            <div>
              <Label>Account Type</Label>
              <RadioGroup value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="customer" data-testid="reg-role-customer" />Customer</label>
                <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="partner" data-testid="reg-role-partner" />Partner</label>
              </RadioGroup>
            </div>
            {form.role === "partner" && (
              <div><Label>Company</Label><Input value={form.company} onChange={update("company")} data-testid="reg-company" /></div>
            )}
            <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="reg-submit">
              {loading ? "..." : t("register")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
